import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, addDoc, collection, setDoc, Timestamp } from 'firebase/firestore'; 
import Preloader from '../components/Preloader';
import html2canvas from 'html2canvas';

const WorkerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // --- STATES ---
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [base64Image, setBase64Image] = useState(""); 
  const [userRating, setUserRating] = useState(0); 
  const [hoverRating, setHoverRating] = useState(0); 
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  // ✅ HIRE SYSTEM STATES
  const [showHireButton, setShowHireButton] = useState(false);
  const [showHireModal, setShowHireModal] = useState(false);
  const [hireDays, setHireDays] = useState(1);
  const [isHiring, setIsHiring] = useState(false);

  // BOOKING STATES
  const [showModal, setShowModal] = useState(false);
  const [bookingData, setBookingData] = useState({ customerName: '', workDate: '', workType: '' });
  const [bookingLoading, setBookingLoading] = useState(false);

  const idCardRef = useRef(null);

  // 🔥 IMAGE CONVERTER
  const convertImageToBase64 = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width; canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve("https://cdn-icons-png.flaticon.com/512/9187/9187604.png");
      img.src = url;
    });
  };

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchWorker = async () => {
        const user = auth.currentUser;
        setCurrentUser(user);

        try {
            const docRef = doc(db, "workers", id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setWorker({ id: docSnap.id, ...data });

                if (user && data.ratings) {
                    const myRating = data.ratings.find(r => r.uid === user.uid);
                    if (myRating) setUserRating(myRating.stars);
                }

                if (data.imageUrl) {
                    const b64 = await convertImageToBase64(data.imageUrl);
                    setBase64Image(b64);
                }

                // ✅ Check if this user called this worker in last 24 hours
                if (user) {
                    const hireRef = doc(db, "potential_hires", `${user.uid}_${id}`);
                    const hireSnap = await getDoc(hireRef);
                    if (hireSnap.exists()) {
                        const hireData = hireSnap.data();
                        const now = Date.now();
                        const expiry = hireData.expiryTime.toMillis();
                        if (now < expiry && !data.isHired) {
                            setShowHireButton(true);
                        }
                    }
                }
            }
        } catch (error) { console.error("Error fetching worker:", error); }
        setLoading(false);
    };
    fetchWorker();
  }, [id]);

  // 🔥 HANDLE CALL TRIGGER
  const handleCallClick = async () => {
    if (!currentUser) {
        alert("Please Login to call/hire!");
        return;
    }
    
    const hireRef = doc(db, "potential_hires", `${currentUser.uid}_${id}`);
    await setDoc(hireRef, {
        customerId: currentUser.uid,
        workerId: id,
        expiryTime: Timestamp.fromMillis(Date.now() + 24 * 60 * 60 * 1000)
    });
    
    setShowHireButton(true);
    window.location.href = `tel:${worker.mobile}`;
  };

  // 🔥 HANDLE FINAL HIRE
  const handleConfirmHire = async () => {
    setIsHiring(true);
    try {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + parseInt(hireDays));

        await updateDoc(doc(db, "workers", id), {
            isHired: true,
            hiredBy: currentUser.uid,
            hiredUntil: Timestamp.fromDate(expiryDate)
        });

        await addDoc(collection(db, "hired_records"), {
            customerId: currentUser.uid,
            workerId: id,
            workerName: worker.name,
            days: hireDays,
            hiredAt: new Date(),
            hiredUntil: Timestamp.fromDate(expiryDate),
            status: 'active'
        });

        alert(`Success! ${worker.name} hired for ${hireDays} days.`);
        setShowHireModal(false);
        navigate('/profile');
    } catch (e) { alert("Error hiring worker"); }
    setIsHiring(false);
  };

  const handleRating = async (stars) => {
    if (!currentUser) { alert("Please Login!"); navigate('/signup'); return; }
    setIsSubmittingRating(true);
    try {
        const workerRef = doc(db, "workers", id);
        let currentRatings = (worker.ratings || []).filter(r => r.uid !== currentUser.uid);
        currentRatings.push({ uid: currentUser.uid, stars: stars });
        const avgRating = (currentRatings.reduce((acc, curr) => acc + curr.stars, 0) / currentRatings.length).toFixed(1);
        await updateDoc(workerRef, { ratings: currentRatings, avgRating: parseFloat(avgRating), ratingCount: currentRatings.length });
        setWorker(prev => ({ ...prev, ratings: currentRatings, avgRating: avgRating, ratingCount: currentRatings.length }));
        setUserRating(stars);
    } catch (e) { console.error(e); }
    setIsSubmittingRating(false);
  };

  const handleBookingChange = (e) => setBookingData({...bookingData, [e.target.name]: e.target.value});

  const handleBookNow = async (e) => {
      e.preventDefault();
      setBookingLoading(true);
      try {
          await addDoc(collection(db, "bookings"), {
              workerId: worker.id, workerName: worker.name, workerMobile: worker.mobile,
              customerName: bookingData.customerName, workDate: bookingData.workDate,
              workType: bookingData.workType, status: 'pending', createdAt: new Date()
          });
          window.open(`https://wa.me/91${worker.mobile}?text=Namaste ${worker.name}, booking request...`, '_blank');
          setShowModal(false);
      } catch (error) { alert("Booking Error."); }
      setBookingLoading(false);
  };

  const downloadIDCard = async () => {
    if (!idCardRef.current) return;
    setDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 800));
      const canvas = await html2canvas(idCardRef.current, { useCORS: true, allowTaint: true, backgroundColor: "#ffffff", scale: 3 });
      const image = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.href = image; link.download = `ID_${worker.name}.png`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (e) { alert("ID Generation failed."); }
    setDownloading(false);
  };

  if (loading) return <Preloader />;
  if (!worker) return <div className="text-center mt-20 text-xl font-bold">Worker Not Found</div>;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-12 px-4 relative" style={{paddingTop : '100px'}}>
      
      {/* 🛑 HIRE MODAL */}
      {showHireModal && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in">
              <div className="bg-white rounded-[2.5rem] p-8 w-full max-w-sm text-center shadow-2xl">
                  <div className="text-5xl mb-4">🤝</div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">Hire {worker.name}?</h2>
                  <p className="text-slate-500 text-sm mb-6">Kitne din ka kaam hai? Itne din ye kisi aur ko nahi dikhega.</p>
                  
                  <div className="flex items-center justify-center gap-6 mb-8 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <button onClick={() => setHireDays(Math.max(1, hireDays-1))} className="w-10 h-10 rounded-full bg-white shadow-md text-xl font-bold">-</button>
                      <span className="text-3xl font-black text-blue-600">{hireDays} <span className="text-sm font-bold text-slate-400">Days</span></span>
                      <button onClick={() => setHireDays(hireDays+1)} className="w-10 h-10 rounded-full bg-white shadow-md text-xl font-bold">+</button>
                  </div>

                  <div className="flex gap-3">
                      <button onClick={() => setShowHireModal(false)} className="flex-1 py-3 bg-slate-100 rounded-xl font-bold text-slate-600">Cancel</button>
                      <button onClick={handleConfirmHire} disabled={isHiring} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg">
                          {isHiring ? 'Wait...' : 'Confirm Hire'}
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* BOOKING MODAL */}
      {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
              <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
                  <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-slate-800">📅 Book {worker.name}</h2>
                      <button onClick={() => setShowModal(false)} className="bg-slate-100 w-8 h-8 rounded-full text-slate-500 transition">✕</button>
                  </div>
                  <form onSubmit={handleBookNow} className="space-y-4">
                      <input required name="customerName" placeholder="Your Name" onChange={handleBookingChange} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500" />
                      <input required name="workDate" type="date" onChange={handleBookingChange} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500" />
                      <textarea required name="workType" placeholder="Kaam kya hai?" onChange={handleBookingChange} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500 h-24"></textarea>
                      <button disabled={bookingLoading} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold text-lg">Send Booking via WhatsApp</button>
                  </form>
              </div>
          </div>
      )}

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        
        {/* LEFT SIDE: WORKER INFO */}
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-slate-100">
          <div className="flex flex-col items-center text-center">
            
            <div className="relative">
                <img src={worker.imageUrl || "https://cdn-icons-png.flaticon.com/512/9187/9187604.png"} alt={worker.name} className="w-40 h-40 rounded-full object-cover border-8 border-slate-50 shadow-lg mb-6" crossOrigin="anonymous" />
                {worker.experience && <span className="absolute bottom-6 right-0 bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full shadow-md border-2 border-white">{worker.experience} Yr Exp</span>}
            </div>

            <h1 className="text-4xl font-extrabold text-slate-800 mb-2">{worker.name}</h1>
            
            <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1 rounded-full border border-yellow-200 mb-4 shadow-sm">
                <span className="text-yellow-500 text-lg">★</span>
                <span className="font-bold text-slate-800">{worker.avgRating || "New"}</span>
                {worker.ratingCount > 0 && <span className="text-xs text-slate-400 ml-1">({worker.ratingCount} reviews)</span>}
            </div>

            <p className="text-slate-500 font-medium text-lg mb-6 flex items-center justify-center gap-2">📍 {worker.village}, {worker.district}</p>
            
            <div className="flex flex-wrap gap-2 justify-center mb-8">
               {worker.skills && Array.isArray(worker.skills) ? worker.skills.map((s, i) => (
                  <span key={i} className="bg-blue-50 text-blue-700 border border-blue-100 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">{s}</span>
               )) : <span className="bg-blue-50 text-blue-700 border border-blue-100 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">{worker.category}</span>}
            </div>

            <div className="w-full space-y-3">
                {showHireButton && !worker.isHired && (
                    <button 
                        onClick={() => setShowHireModal(true)}
                        className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-5 rounded-2xl font-black text-xl shadow-xl animate-pulse flex items-center justify-center gap-3"
                    >
                        ⚡ HIRE NOW
                    </button>
                )}

                <button onClick={() => setShowModal(true)} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:-translate-y-1 transition">📅 Book Now</button>
                <div className="grid grid-cols-2 gap-3">
                    <a href={`https://wa.me/91${worker.mobile}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-xl font-bold hover:bg-[#20b857] transition shadow-md text-sm">💬 WhatsApp</a>
                    <button onClick={handleCallClick} className="flex items-center justify-center gap-2 bg-slate-800 text-white py-3 rounded-xl font-bold hover:bg-slate-900 transition shadow-md text-sm">📞 Call Now</button>
                </div>
            </div>

            {/* RATE THIS WORKER */}
            <div className="w-full bg-slate-50 rounded-2xl p-6 mt-8 border border-slate-100 text-center">
                <h3 className="text-slate-800 font-bold mb-3">Rate this Worker</h3>
                <div className="flex justify-center gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button 
                            key={star} 
                            disabled={isSubmittingRating}
                            onClick={() => handleRating(star)} 
                            onMouseEnter={() => setHoverRating(star)} 
                            onMouseLeave={() => setHoverRating(0)} 
                            className={`text-4xl transition-transform hover:scale-125 focus:outline-none ${star <= (hoverRating || userRating) ? 'text-yellow-400' : 'text-slate-300'} ${isSubmittingRating ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            ★
                        </button>
                    ))}
                </div>
                {isSubmittingRating && <p className="text-[10px] text-blue-500 font-bold animate-pulse">Updating rating...</p>}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: DIGITAL ID CARD */}
        <div className="flex flex-col items-center justify-center lg:sticky lg:top-24">
            <h3 className="text-2xl font-bold text-slate-800 mb-4 text-center">🆔 Official ID Card</h3>
            <div ref={idCardRef} className="w-[340px] aspect-[3/4.6] bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-900 rounded-[2.5rem] overflow-hidden shadow-2xl relative text-white flex flex-col p-1 border-4 border-white">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"></div>
                <div className="bg-white/10 backdrop-blur-md py-6 text-center border-b border-white/10 z-10"><h2 className="text-2xl font-black leading-none uppercase">KaamSetu</h2><p className="text-[9px] uppercase tracking-[0.3em] opacity-80 mt-1 font-bold">Identity & Verification Card</p></div>
                <div className="flex-1 p-6 flex flex-col items-center text-center relative z-10">
                    <div className="relative mb-4"><div className="absolute inset-0 bg-yellow-400 rounded-full blur-xl opacity-30 animate-pulse"></div><img src={base64Image || "https://cdn-icons-png.flaticon.com/512/9187/9187604.png"} alt="Profile" className="relative w-32 h-32 rounded-full object-cover border-4 border-white shadow-xl" /><div className="absolute -bottom-2 right-1 bg-green-500 border-2 border-white w-6 h-6 rounded-full flex items-center justify-center text-[10px] shadow-lg">✅</div></div>
                    <h2 className="text-3xl font-bold mb-1 tracking-tight">{worker.name}</h2>
                    <div className="text-yellow-400 text-sm font-bold mb-4 drop-shadow-md">★ ★ ★ ★ ★</div>
                    <div className="mb-6"><span className="bg-yellow-400 text-black px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-md">{worker.skills && Array.isArray(worker.skills) ? worker.skills[0] : worker.category}</span></div>
                    <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 w-full mb-5 border border-white/20"><div className="grid grid-cols-2 gap-2 text-left"><div><p className="text-[9px] uppercase opacity-60 font-black">Experience</p><p className="font-bold text-sm">{worker.experience || "0"} Years</p></div><div><p className="text-[9px] uppercase opacity-60 font-black">Location</p><p className="font-bold text-sm truncate">{worker.district}</p></div></div></div>
                    <div className="bg-white text-blue-900 px-6 py-3 rounded-2xl font-black text-lg w-full flex justify-center shadow-2xl">📞 {worker.mobile}</div>
                </div>
                <div className="bg-slate-900/80 backdrop-blur-md p-4 text-center z-10 border-t border-white/10"><p className="text-[10px] text-slate-400 uppercase tracking-[0.2em]">Verified by www.indlabour.xyz</p></div>
            </div>
            <button onClick={downloadIDCard} disabled={downloading} className={`mt-8 px-10 py-4 rounded-full font-black shadow-2xl flex items-center gap-3 transition-all transform hover:scale-105 active:scale-95 border-b-4 ${downloading ? "bg-slate-400" : "bg-gradient-to-r from-blue-600 to-indigo-700 text-white"}`}>
                {downloading ? <>⏳ Saving...</> : <>⬇️ Download ID Card</>}
            </button>
        </div>
      </div>
    </div>
  );
};

export default WorkerDetails;